import apiClient from './client';
import type { Business, ServiceType, Invitation, Provider, SubscriptionPlansResponse, Customer, PageResponse, OwnerAnalyticsData } from '../types/business';
import type { ApiResponse } from '../types/api';
import type { TranslationStatusEntry, ServiceTypeTranslation } from '../types/i18n';
import type { AnalyticsPeriod } from '../types/provider';

export const businessApi = {
  getMyBusinesses: async (): Promise<Business[]> => {
    const res = await apiClient.get<ApiResponse<Business[]>>('/v1/owner/tenants');
    return res.data.data;
  },

  getCustomersCount: async (tenantId: number): Promise<number> => {
    const res = await apiClient.get<ApiResponse<number>>(`/v1/owner/tenants/${tenantId}/stats/customers-count`);
    return res.data.data;
  },

  getCustomers: async (tenantId: number, page = 0, size = 10): Promise<PageResponse<Customer>> => {
    const res = await apiClient.get<ApiResponse<PageResponse<Customer>>>(`/v1/owner/tenants/${tenantId}/customers`, {
      params: { page, size },
    });
    return res.data.data;
  },

  getTenant: async (tenantId: number): Promise<Business> => {
    const res = await apiClient.get<ApiResponse<Business>>(`/v1/owner/tenants/${tenantId}`);
    return res.data.data;
  },

  updateTenant: async (tenantId: number, data: {
    businessName?: string;
    defaultLanguage?: string;
    supportedLanguages?: string[];
    manualConfirmTimeoutMinutes?: number;
    welcomeMessage?: string | null;
  }): Promise<Business> => {
    const res = await apiClient.patch<ApiResponse<Business>>(`/v1/owner/tenants/${tenantId}`, data);
    return res.data.data;
  },

  updateSettings: async (tenantId: number, data: {
    manualConfirmTimeoutMinutes?: number;
    welcomeMessage?: string | null;
    slotStepMinutes?: number;
    defaultServiceDurationMinutes?: number;
  }): Promise<Business> => {
    const res = await apiClient.put<ApiResponse<Business>>(`/v1/owner/tenants/${tenantId}/settings`, data);
    return res.data.data;
  },

  uploadLogo: async (tenantId: number, file: File): Promise<Business> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<ApiResponse<Business>>(`/v1/owner/tenants/${tenantId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },

  deleteLogo: async (tenantId: number): Promise<Business> => {
    const res = await apiClient.delete<ApiResponse<Business>>(`/v1/owner/tenants/${tenantId}/logo`);
    return res.data.data;
  },

  getTranslationStatus: async (tenantId: number): Promise<TranslationStatusEntry[]> => {
    const res = await apiClient.get<ApiResponse<TranslationStatusEntry[]>>(
      `/v1/owner/tenants/${tenantId}/translation-status`,
    );
    return res.data.data;
  },

  getAnalytics: async (tenantId: number, period: AnalyticsPeriod): Promise<OwnerAnalyticsData> => {
    const res = await apiClient.get<ApiResponse<OwnerAnalyticsData>>(
      `/v1/owner/tenants/${tenantId}/analytics`,
      { params: { period } },
    );
    return res.data.data;
  },
};

export interface CheckoutResponse {
  merchantTransId: string;
  checkoutUrl: string;
  plan: string;
  durationMonths: number;
  amountTiyin: number;
  expiresAt: string;
}

export const subscriptionApi = {
  getPlans: async (tenantId: number): Promise<SubscriptionPlansResponse> => {
    const res = await apiClient.get<ApiResponse<SubscriptionPlansResponse>>('/v1/subscription-plans', {
      params: { tenantId },
    });
    return res.data.data;
  },

  checkout: async (tenantId: number, data: {
    plan: string;
    durationMonths: number;
    returnUrl?: string;
  }): Promise<CheckoutResponse> => {
    const res = await apiClient.post<ApiResponse<CheckoutResponse>>(
      `/v1/owner/tenants/${tenantId}/subscription/checkout`,
      data,
    );
    return res.data.data;
  },
};

export const providerApi = {
  getAll: async (tenantId: number, activeOnly = false): Promise<Provider[]> => {
    const res = await apiClient.get<ApiResponse<Provider[]>>(`/v1/owner/tenants/${tenantId}/providers`, {
      params: { activeOnly },
    });
    return res.data.data;
  },

  activate: async (tenantId: number, id: number): Promise<Provider> => {
    const res = await apiClient.put<ApiResponse<Provider>>(`/v1/owner/tenants/${tenantId}/providers/${id}/activate`);
    return res.data.data;
  },

  deactivate: async (tenantId: number, id: number): Promise<Provider> => {
    const res = await apiClient.put<ApiResponse<Provider>>(`/v1/owner/tenants/${tenantId}/providers/${id}/deactivate`);
    return res.data.data;
  },

  block: async (tenantId: number, id: number): Promise<Provider> => {
    const res = await apiClient.put<ApiResponse<Provider>>(`/v1/owner/tenants/${tenantId}/providers/${id}/block`);
    return res.data.data;
  },

  unblock: async (tenantId: number, id: number): Promise<Provider> => {
    const res = await apiClient.put<ApiResponse<Provider>>(`/v1/owner/tenants/${tenantId}/providers/${id}/unblock`);
    return res.data.data;
  },

  remove: async (tenantId: number, id: number): Promise<void> => {
    await apiClient.delete(`/v1/owner/tenants/${tenantId}/providers/${id}`);
  },
};

export const invitationApi = {
  getAll: async (tenantId: number): Promise<Invitation[]> => {
    const res = await apiClient.get<ApiResponse<Invitation[]>>(`/v1/owner/tenants/${tenantId}/invitations`);
    return res.data.data;
  },

  invite: async (tenantId: number, phoneNumber: string): Promise<Invitation> => {
    const res = await apiClient.post<ApiResponse<Invitation>>(`/v1/owner/tenants/${tenantId}/invitations`, { phoneNumber });
    return res.data.data;
  },

  revoke: async (tenantId: number, invitationId: number): Promise<void> => {
    await apiClient.delete(`/v1/owner/tenants/${tenantId}/invitations/${invitationId}`);
  },
};

export const serviceTypeApi = {
  getAll: async (tenantId: number, activeOnly = false): Promise<ServiceType[]> => {
    const res = await apiClient.get<ApiResponse<ServiceType[]>>(`/v1/owner/tenants/${tenantId}/service-types`, {
      params: { activeOnly },
    });
    return res.data.data;
  },

  create: async (tenantId: number, data: { name: string; description?: string; translations?: ServiceTypeTranslation[] }): Promise<ServiceType> => {
    const res = await apiClient.post<ApiResponse<ServiceType>>(`/v1/owner/tenants/${tenantId}/service-types`, data);
    return res.data.data;
  },

  update: async (tenantId: number, id: number, data: { name?: string; description?: string; translations?: ServiceTypeTranslation[] }): Promise<ServiceType> => {
    const res = await apiClient.put<ApiResponse<ServiceType>>(`/v1/owner/tenants/${tenantId}/service-types/${id}`, data);
    return res.data.data;
  },

  activate: async (tenantId: number, id: number): Promise<ServiceType> => {
    const res = await apiClient.put<ApiResponse<ServiceType>>(`/v1/owner/tenants/${tenantId}/service-types/${id}/activate`);
    return res.data.data;
  },

  deactivate: async (tenantId: number, id: number): Promise<ServiceType> => {
    const res = await apiClient.put<ApiResponse<ServiceType>>(`/v1/owner/tenants/${tenantId}/service-types/${id}/deactivate`);
    return res.data.data;
  },

  remove: async (tenantId: number, id: number): Promise<void> => {
    await apiClient.delete(`/v1/owner/tenants/${tenantId}/service-types/${id}`);
  },
};
